import React, { useEffect, useMemo } from "react";
import { ModalState } from "./Photos";
import { Form, Modal } from "react-bootstrap";
import { Photo } from "../../Contract/Photos";
import FileDropzone from "../../Components/FileDropZone";
import Button from "../../Components/Button";
import { PhotosApi } from "../../services/photoService";
import { v4 } from "uuid";

type EditingPhoto = Photo & {
  image: File | null;
};

type Props = {
  state: ModalState;
  setState: React.Dispatch<React.SetStateAction<ModalState>>;
  onSaved?: () => void;
};

export default function EditPhotoModal({ state, setState, onSaved }: Props) {
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
  const [localState, setLocalState] = React.useState<EditingPhoto>(
    state.photo
      ? { ...state.photo, image: null }
      : {
          id: v4(),
          url: "",
          date: "",
          name: "",
          description: "",
          image: null,
        },
  );

  useEffect(() => {
    setLocalState(
      state.photo
        ? { ...state.photo, image: null }
        : {
            id: v4(),
            name: "",
            url: "",
            description: "",
            date: "",
            image: null,
          },
    );
  }, [state.photo]);

  const [previewUrl, setPreviewUrl] = React.useState<string>("");

  const handleSave = async () => {
    setError(null);
    const photosApi = new PhotosApi();
    try {
      setSaving(true);

      const meta = {
        name: localState.name,
        description: localState.description,
        date: localState.date,
      };

      // CREATE: must have an image
      if (!state.photo) {
        if (!localState.image) {
          setError("Please choose an image to upload.");
          return;
        }

        await photosApi.createAndUploadPhoto(localState.image, meta);
        await onSaved?.();
        setState({ open: false });
        return;
      }

      // EDIT/REPLACE:
      // - if user picked a new file => replace image + update metadata
      if (localState.image) {
        await photosApi.replaceAndUploadPhoto(
          state.photo.id,
          localState.image,
          meta,
        );
        await onSaved?.();
        setState({ open: false });
        return;
      }

      // Metadata-only edit (no new image)
      // You currently DON'T have a backend action for this.
      // Options:
      // 1) Add a new lambda action updatePhotoDetails (recommended) and call it here.
      // 2) Force users to pick a new image to save changes.
      setError(
        "To update details, please pick a new image (or add a metadata-only endpoint).",
      );
    } catch (e: any) {
      console.log(e);
      setError(e?.message ?? "Failed to save photo.");
    } finally {
      setSaving(false);
      setShowConfirmDelete(false);
      setState({ open: false });
    }
  };

  useEffect(() => {
    if (localState.image) {
      const url = URL.createObjectURL(localState.image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(localState.url);
  }, [localState.image, localState.url]);

  if (showConfirmDelete) {
    return (
      <Modal
        show={showConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
      >
        <Modal.Header closeButton>Confirm Delete</Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the photo "{state.photo?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="p-2 w-25 secondary-button"
            onClick={() => setShowConfirmDelete(false)}
          >
            Cancel
          </Button>
          <Button
            className="p-2 w-25 danger-button"
            onClick={async () => {
              if (!state.photo) return;
              setSaving(true);
              setError(null);
              try {
                const photosApi = new PhotosApi();
                const result = await photosApi.deletePhoto(state.photo.id);
                if (result.ok && result.deleted) {
                  setState({ open: false });
                  await onSaved?.();
                } else {
                  setError("Failed to delete photo.");
                }
              } catch (e: any) {
                console.log(e);
                setError(e?.message ?? "Failed to delete photo.");
              } finally {
                setSaving(false);
              }
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
  return (
    <Modal
      show={state.open}
      onHide={() =>
        setState({
          open: false,
        })
      }
    >
      <Modal.Header closeButton>
        {state.photo ? state.photo.name : "Add Photo"}
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div
            className={`${localState?.image || localState.url ? "d-flex" : "d-none"} justify-content-center`}
          >
            <img className="image-preview" src={previewUrl} />
          </div>
          <FileDropzone
            className="mb-2"
            onFiles={(files) =>
              setLocalState({ ...localState, image: files[0] })
            }
          />
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              onChange={(e) =>
                setLocalState({ ...localState, name: e.target.value })
              }
              value={localState.name}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label>Description</Form.Label>
            <Form.Control
              onChange={(e) =>
                setLocalState({ ...localState, description: e.target.value })
              }
              value={localState.description}
            />
          </Form.Group>
        </Form>
        {error && <div className="text-danger mt-2">{error}</div>}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button
          className="p-2 w-25 danger-button"
          onClick={() => setShowConfirmDelete(true)}
        >
          Delete
        </Button>
        <Button
          className="p-2 w-25 primary-button"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
